import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { logOut } from "@actions/userActions";
import { fetchOfflineProvisioningStatus } from "@utils/api-communication/OfflineProvisioningCli";
import { SecureTokenManager } from "@utils/auth/secureTokenManager";
import { isOfflineFrontendMode } from "@utils/deviceSetupNavigation";
import { navigate } from "@utils/navigationService";

export const OFFLINE_ACTIVATION_ROUTE = "/activate";

// Offline mode otherwise routes to the login page, which a freshly installed
// system can never get past: activation is what creates the first user.
export default function OfflineActivationGate() {
  const dispatch = useDispatch();
  const location = useLocation();
  const activationConfirmed = useRef(false);

  useEffect(() => {
    if (!isOfflineFrontendMode() || activationConfirmed.current) {
      return;
    }

    let cancelled = false;

    fetchOfflineProvisioningStatus()
      .then((status) => {
        if (cancelled) {
          return;
        }
        const isOnActivationRoute = location.pathname === OFFLINE_ACTIVATION_ROUTE;

        if (!status.activated) {
          if (SecureTokenManager.isAuthenticated()) {
            dispatch(logOut(OFFLINE_ACTIVATION_ROUTE));
            return;
          }
          if (!isOnActivationRoute) {
            navigate(OFFLINE_ACTIVATION_ROUTE);
          }
          return;
        }

        // Once activated nothing can un-activate the system, so stop checking.
        activationConfirmed.current = true;
        if (isOnActivationRoute) {
          navigate("/login");
        }
      })
      .catch(() => {
        // Redirecting on an unknown state could strand an activated system on the
        // activation screen, so leave it for the next navigation to retry.
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, location.pathname]);

  return null;
}
